import { create } from 'zustand';
import { useLabelStore } from './useLabelStore';
import { avecDecoupe, FOND_PERDU_DEFAUT_MM } from '../utils/decoupe';

// Document vide par défaut : 130×130 mm (carré), fond blanc, règles affichées,
// 1,5 mm de fond perdu par côté (3 mm au total) comme toute étiquette à imprimer.
// Cohérent avec les valeurs par défaut de useLabelStore.
const DEFAULT_CANVAS = {
  width_mm: 130, height_mm: 130, color_bg: '#FFFFFF', scale: 3,
  margin_mm: 3, bleed_mm: FOND_PERDU_DEFAUT_MM, showRulers: true, showGuides: false, showGrid: false,
  panX: 0, panY: 0, snapToGrid: true, snapToGuides: true,
  showMargin: true, showBleed: true,
};

const generateProjectName = (canvas = DEFAULT_CANVAS) => {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yy = String(now.getFullYear()).slice(2);
  const w = canvas.width_mm || 130;
  const h = canvas.height_mm || 80;
  return `${dd}-${mm}-${yy}-${w}x${h}`;
};

const nouvelIdCalque = () => `el_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

// Un onglet VIERGE naît avec sa découpe (utils/decoupe). Un onglet qui reçoit un
// projet (fichier ouvert, gabarit, import) le garde tel quel : `addTab`,
// `addTabsBatch` et `init` remplacent les calques juste après.
const createEmptyTab = (name = null, canvas = null) => {
  const c = canvas || { ...DEFAULT_CANVAS };
  return {
    id: `tab_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    name: name || generateProjectName(c),
    isDirty: false,
    canvas: c,
    elements: avecDecoupe([], c, nouvelIdCalque),
    aiImage: { url: null, x: 100, y: 50, width: 200, height: 150 },
    selectedElementIds: [],
    history: [],
    historyIndex: -1,
  };
};

export const useTabsStore = create((set, get) => ({
  tabs: [createEmptyTab()],
  activeTabId: null, // set on init

  // ── Getters ──

  getActiveTab: () => {
    const { tabs, activeTabId } = get();
    return tabs.find(t => t.id === activeTabId) || tabs[0] || null;
  },

  getTab: (id) => get().tabs.find(t => t.id === id) || null,

  // ── Tab lifecycle ──

  /**
   * Ouvre l'onglet de départ à partir du document RÉHYDRATÉ.
   *
   * À n'appeler qu'après la réhydratation d'IndexedDB (cf. App.jsx) : appelée
   * avant, elle ouvrait un onglet vide qui écrasait le document et empoisonnait
   * l'historique — un Ctrl+Z après un F5 vidait l'étiquette.
   *
   * Elle lisait le projet dans localStorage ; la clé n'y est plus depuis le
   * passage à IndexedDB, qui la supprime après migration. On lit donc
   * directement le document, seule source de vérité.
   */
  init: () => {
    const { tabs } = get();

    try {
      const doc = useLabelStore.getState();
      if (doc?.elements?.length > 0) {
        const canvas = doc.canvas || { ...DEFAULT_CANVAS };
        const tab = createEmptyTab(null, canvas);
        tab.elements = JSON.parse(JSON.stringify(doc.elements));
        tab.aiImage = doc.aiImage || tab.aiImage;
        tab.isDirty = false;
        set({ tabs: [tab], activeTabId: tab.id });
        return;
      }
    } catch (e) { /* document illisible → onglet vierge */ }

    // Aucun document restauré — onglet vierge.
    if (tabs.length === 0) {
      const tab = createEmptyTab();
      set({ tabs: [tab], activeTabId: tab.id });
    } else if (!get().activeTabId) {
      set({ activeTabId: tabs[0].id });
    }
  },

  addTab: (name = null, projectData = null) => {
    // Les réglages absents du projet (vue, repères) prennent leur défaut, comme dans addTabsBatch :
    // un canvas sans `scale` ouvrait un onglet au zoom mort (document de l'agent, 16/10).
    const canvas = { ...DEFAULT_CANVAS, ...(projectData?.canvas || {}) };
    const tab = createEmptyTab(name, canvas);
    if (projectData) {
      tab.elements = projectData.elements || [];
      tab.aiImage = projectData.aiImage || tab.aiImage;
      tab.isDirty = false;
    }
    set(s => ({
      tabs: [...s.tabs, tab],
      activeTabId: tab.id,
    }));
    return tab.id;
  },

  // Ajoute PLUSIEURS onglets en UNE seule mutation (import par lot). Évite les
  // races avec useTabSync, qui réagit à chaque changement d'activeTabId — ici
  // une seule transition. Active le 1er onglet ajouté par défaut.
  // items: [{ name, canvas, elements, aiImage }]
  addTabsBatch: (items, { activateFirst = true } = {}) => {
    const list = (items || []).filter(Boolean);
    if (list.length === 0) return [];
    const newTabs = list.map((pd) => {
      const canvas = { ...DEFAULT_CANVAS, ...(pd.canvas || {}) };
      const tab = createEmptyTab(pd.name || null, canvas);
      tab.elements = pd.elements || [];
      tab.aiImage = pd.aiImage || tab.aiImage;
      tab.isDirty = false;
      // Import PDF : les cadres doivent se resserrer sur leur contenu. On ne
      // peut pas le faire ici — un calque se MESURE à l'écran, et seul l'onglet
      // actif est monté. L'onglet porte donc la consigne, et useTabSync
      // l'exécute au moment où il devient visible.
      if (pd.ajusterAuContenu) tab.ajusterAuContenu = true;
      return tab;
    });
    set((s) => ({
      tabs: [...s.tabs, ...newTabs],
      activeTabId: activateFirst ? newTabs[0].id : s.activeTabId,
    }));
    return newTabs.map((t) => t.id);
  },

  closeTab: (id) => {
    const { tabs, activeTabId } = get();
    const tab = tabs.find(t => t.id === id);
    if (!tab) return false;

    // Confirm if dirty
    if (tab.isDirty) {
      if (!window.confirm(`"${tab.name}" a des modifications non sauvegardées. Fermer quand même ?`)) {
        return false;
      }
    }

    const remaining = tabs.filter(t => t.id !== id);

    if (remaining.length === 0) {
      // Last tab: create a new empty one with auto name
      const newTab = createEmptyTab();
      set({ tabs: [newTab], activeTabId: newTab.id });
    } else {
      // Switch to neighbor tab if closing active
      let newActiveId = activeTabId;
      if (activeTabId === id) {
        const idx = tabs.findIndex(t => t.id === id);
        newActiveId = remaining[Math.min(idx, remaining.length - 1)].id;
      }
      set({ tabs: remaining, activeTabId: newActiveId });
    }
    return true;
  },

  switchTab: (id) => {
    const { tabs } = get();
    if (tabs.find(t => t.id === id)) {
      set({ activeTabId: id });
    }
  },

  renameTab: (id, name) => {
    set(s => ({
      tabs: s.tabs.map(t => t.id === id ? { ...t, name } : t),
    }));
  },

  markDirty: (id) => {
    set(s => ({
      tabs: s.tabs.map(t => t.id === (id || s.activeTabId) ? { ...t, isDirty: true } : t),
    }));
  },

  markClean: (id) => {
    set(s => ({
      tabs: s.tabs.map(t => t.id === (id || s.activeTabId) ? { ...t, isDirty: false } : t),
    }));
  },

  // ── Update active tab data (called by useLabelStore proxy) ──

  updateActiveTabData: (partial) => {
    const { activeTabId } = get();
    if (!activeTabId) return;
    set(s => ({
      tabs: s.tabs.map(t => t.id === activeTabId ? { ...t, ...partial, isDirty: true } : t),
    }));
  },

  // ── Reorder tabs (drag & drop) ──

  reorderTabs: (fromIndex, toIndex) => {
    set(s => {
      const newTabs = [...s.tabs];
      const [moved] = newTabs.splice(fromIndex, 1);
      newTabs.splice(toIndex, 0, moved);
      return { tabs: newTabs };
    });
  },

  // ── Check if any tab is dirty (for beforeunload) ──

  hasUnsavedChanges: () => get().tabs.some(t => t.isDirty),
}));
